import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Flex, Spinner, Text } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { FiBookOpen } from "react-icons/fi";
import { AppBar, APP } from "../../components/mobileUi";
import {
  DesktopBack,
  DesktopPage,
  DesktopPanel,
  useIsDesktop,
} from "../../components/desktopUi";
import {
  ActionPill,
  CardBox,
  CardHeaderIcon,
  ClampDescription,
} from "./trainingUi";
import { getTrainingTopicsApi } from "../../api/training.api";
import { EMP_BASE_PATH } from "../../config";

const LIMIT = 10;
const TEXT = "#18212F";
const MUTED = "#94A3B8";

const TopicCard = ({ item, onClick }) => (
  <CardBox onClick={onClick}>
    <Flex align="center" gap="8px">
      <CardHeaderIcon icon={<FiBookOpen />} />
      <Text fontSize="14px" fontWeight={600} color={TEXT} noOfLines={1} flex="1">
        {item.name}
      </Text>
    </Flex>
    <Box h="10px" />
    <ClampDescription html={item.description} />
    <Box h="12px" />
    <Flex justify="flex-end">
      <ActionPill />
    </Flex>
  </CardBox>
);

const TopicList = () => {
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(false);
  const reqId = useRef(0);

  const fetchPage = useCallback(async (p, mode) => {
    reqId.current += 1;
    const rid = reqId.current;
    setLoading(true);
    try {
      const data = await getTrainingTopicsApi({ page: p, limit: LIMIT });
      if (rid !== reqId.current) return;
      const list = data?.items || [];
      setItems((prev) => (mode === "append" ? [...prev, ...list] : list));
      setHasNext(!!data?.pagination?.hasNextPage);
      setPage(p);
    } catch {
      if (rid === reqId.current && mode !== "append") setItems([]);
    } finally {
      if (rid === reqId.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPage(1, "replace");
  }, [fetchPage]);

  const pageBody = (
    <>

      <Box px="16px" pt="12px">
        {loading && !items.length ? (
          <Flex justify="center" py="40px">
            <Spinner color={APP.primary200} />
          </Flex>
        ) : items.length === 0 ? (
          <Text fontSize="14px" color={MUTED} textAlign="center" pt="40px">
            Không có chủ đề đào tạo
          </Text>
        ) : (
          <Flex direction="column" gap="14px">
            {items.map((it) => (
              <TopicCard
                key={it._id}
                item={it}
                onClick={() =>
                  navigate(`${EMP_BASE_PATH}/dao-tao/chu-de/${it._id}`, {
                    state: { topicName: it.name },
                  })
                }
              />
            ))}
            {hasNext && (
              <Flex
                as="button"
                onClick={() => fetchPage(page + 1, "append")}
                justify="center"
                py="12px"
                color={APP.primary200}
                fontWeight={600}
                fontSize="14px"
              >
                {loading ? <Spinner size="sm" /> : "Xem thêm"}
              </Flex>
            )}
          </Flex>
        )}
      </Box>
    </>
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    return (
      <DesktopPage title="Danh sách chủ đề" right={<DesktopBack onClick={() => navigate(-1)} />}>
        <DesktopPanel p="0" pb="24px">
          {pageBody}
        </DesktopPanel>
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" pb="40px">
      <AppBar title="Danh sách chủ đề" onBack={() => navigate(EMP_BASE_PATH)} />
      {pageBody}
    </Box>
  );
};

export default TopicList;
